/** @file One project card: thumbnail, name, rev/actor/date line; open, trash, restore and purge. */
import type { Translate } from '@pose2d/i18n/types';
import type { ProjectListItem } from '../../api/apiTypes';
import { ConfirmButton } from '../../ui-kit/ConfirmButton';

const LAZY = 'lazy';

interface ProjectCardProps {
  item: ProjectListItem;
  t: Translate;
  onOpen: (id: string) => void;
  /** Undefined in the trash view. */
  onTrash: ((id: string) => void) | undefined;
  /** True while another create or trash request is in flight; disables the trash button. */
  busy?: boolean;
  /** Trash view: take the project back (project.restore). */
  onRestore?: ((id: string) => void) | undefined;
  /** Trash view: delete for good (two clicks). */
  onPurge?: ((id: string) => void) | undefined;
  /** A just-rendered thumbnail that wins over item.thumbUrl; null while its re-render is pending (no stale request). */
  thumbUrl?: string | null | undefined;
}

/** `onTrash` is undefined in the trash view; `busy` disables the trash button during a request. */
export function ProjectCard({ item, t, onOpen, onTrash, onRestore, onPurge, thumbUrl, busy = false }: ProjectCardProps) {
  const thumb = thumbUrl === undefined ? item.thumbUrl : thumbUrl;
  return (
    <article className="shell-card project-card" data-testid={`project-${item.id}`}>
      <button type="button" className="project-thumb" aria-label={item.name} onClick={() => { onOpen(item.id); }}>
        {thumb === null ? <span className="project-thumb-empty" /> : <img src={thumb} alt="" loading={LAZY} />}
      </button>
      <h2 className="project-name">{item.name}</h2>
      <p className="project-meta">{t('projects.meta', { rev: item.rev, actor: item.lastActor ?? '-', date: item.updatedAt.slice(0, 10) })}</p>
      {item.trashedAt === null ? null : <p className="project-meta">{t('projects.trashedOn', { date: item.trashedAt.slice(0, 10) })}</p>}
      {onTrash === undefined ? null : <button type="button" disabled={busy} onClick={() => { onTrash(item.id); }}>{t('projects.trash')}</button>}
      {onRestore === undefined ? null : <button type="button" disabled={busy} onClick={() => { onRestore(item.id); }}>{t('projects.restore')}</button>}
      {onPurge === undefined ? null : <ConfirmButton label={t('projects.purge')} confirmLabel={t('projects.confirmPurge', { name: item.name })} disabled={busy} onConfirm={() => { onPurge(item.id); }} />}
    </article>
  );
}
